import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { STEPS } from "../data/courseData";
import { STEP_INSIGHTS, INSIGHT_DETAILS, InsightCard } from "../components/InsightVisuals";

const KEY_INSIGHTS = {
  1: ["Pain is rarely just physical.", "The nervous system stores what we cannot say.", "Awareness is the first reset."],
  2: ["Posture is emotion frozen in muscle.", "Breath is the bridge to the body.", "The body adapted — it isn't broken."],
  3: ["The mind rehearses pain like a song.", "Identity is built from repeated thoughts.", "Neuroplasticity allows any pattern to change."],
  4: ["You are not who you were when you arrived.", "Repetition installs the new identity.", "Mind, body, and emotion now operate as one."],
};

export default function VideoPage() {
  const { step } = useParams();
  const stepNum = parseInt(step, 10);
  const meta = STEPS.find((s) => s.step === stepNum);
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [marked, setMarked] = useState(false);

  useEffect(() => {
    api.get("/videos").then((r) => setVideo(r.data.find((v) => v.step === stepNum)));
    api.get("/video-progress").then((r) => {
      const p = r.data.find((v) => v.step === stepNum);
      if (p) setMarked(!!p.watched);
    });
  }, [stepNum]);

  async function markWatched() {
    await api.post("/video-progress", { step: stepNum, watched: true, key_insights: KEY_INSIGHTS[stepNum] });
    setMarked(true);
    // Auto-advance to the Guided Journal (exercise 4) after a brief beat
    setTimeout(() => navigate(`/journey/${stepNum}/journal`), 900);
  }

  return (
    <Layout hideNav>
      <button onClick={() => navigate(`/journey/${stepNum}`)} className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4" data-testid="back-to-step">
        <ArrowLeft size={16} /> Week {stepNum}
      </button>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-5">
        <div>
          <div className="bps-eyebrow" style={{ color: meta?.accent }}>Practice 3 · Video Lesson</div>
          <h1 className="font-display text-3xl font-light tracking-tighter mt-1">{video?.title || "Loading…"}</h1>
        </div>

        <div className="aspect-video rounded-3xl overflow-hidden bg-black shadow-[0_20px_40px_rgb(0,0,0,0.12)]" data-testid="video-frame">
          {video?.url ? (
            <iframe
              title={video.title}
              src={video.url}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">Video coming soon</div>
          )}
        </div>

        <div>
          <div className="bps-eyebrow mb-3">Key insights</div>
          <div className="space-y-3" data-testid="insights-list">
            {(STEP_INSIGHTS[stepNum] || []).map((it, i) => (
              <InsightCard
                key={it.text}
                Visual={it.Visual}
                text={it.text}
                index={i}
                step={stepNum}
                detail={(INSIGHT_DETAILS[stepNum] || [])[i]}
              />
            ))}
          </div>
        </div>

        <button onClick={markWatched} disabled={marked} data-testid="mark-watched" className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold shadow-[0_0_24px_rgba(34,211,238,0.35)] disabled:opacity-60 transition-all hover:-translate-y-0.5">
          {marked ? <span className="inline-flex items-center gap-2"><Check size={16}/>Watched · opening journal…</span> : "Mark as watched & continue to journal"}
        </button>
      </motion.div>
    </Layout>
  );
}
